import { useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RESULT_REQUEST } from 'features/close-circle-edit/close-circle-edit'

import {
  EditDetailForm,
  IDocumentForm,
} from 'entities/user/components/edit-detail-form'
import { DOCUMENT } from 'entities/user/module'
import userStore from 'entities/user/module/user-store'

import { UserApi } from 'shared/api/UserApi'
import { IProfileTypes, ProfileTypes } from 'shared/constants'
import citizenshipStore from 'shared/store/citizenship-store'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { EmptyPage } from 'shared/ui/empty-page'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'

import { SuccessEditModal } from './components/success-edit-modal'
import { selectTypePassport } from './lib/setDefaultValues'
import s from './styles.module.scss'

const EditDetailProfile = observer(() => {
  const { type } = useParams()

  const { t } = useTranslation()

  const { openDialog, closeDialog } = useDialogBottomSheet()
  const isPass = type === ProfileTypes.PASS.key
  const { account } = userStore

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IDocumentForm>({
    defaultValues: {
      ...selectTypePassport(account, isPass, type),
    },
  })

  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    void citizenshipStore.ensureAviaCitizenships()
  }, [])

  const hasCitizenshipsLoadError =
    citizenshipStore.citizenshipAvia === null &&
    citizenshipStore.aviaStatus === 'error'
  const isCitizenshipsInitialLoading =
    citizenshipStore.citizenshipAvia === null && !hasCitizenshipsLoadError

  if (hasCitizenshipsLoadError) {
    return (
      <EmptyPage
        title={t('error')}
        subTitle={t('error_des')}
        buttonText={t('try_again')}
        onClick={() => void citizenshipStore.ensureAviaCitizenships()}
      />
    )
  }

  if (isCitizenshipsInitialLoading) {
    return <Loader marginTop={150} />
  }

  const citizenshipsData = citizenshipStore?.citizenshipAvia?.map(
    (citizenship) => ({
      value: citizenship?.code,
      name: citizenship?.country,
    }),
  )

  const onNavigate = () => {
    navigate(-1)
    closeDialog()
  }

  const showPopUp = (result: RESULT_REQUEST) => {
    openDialog({
      child: (
        <SuccessEditModal
          result={result}
          onNavigate={onNavigate}
        />
      ),
      isDialog: false,
    })
  }

  const onSubmit = async ({ form }: IDocumentForm) => {
    setLoading(true)

    try {
      await UserApi.updateUserDocuments({
        ...form,
        middle_name: form?.hasMiddle ? form.middle_name : '',
        document_type: isPass ? DOCUMENT.ID : DOCUMENT.PASSPORT,
      })
      showPopUp(RESULT_REQUEST.SUCCESS)

      const { data: userData } = await UserApi.getUser()
      userStore.loginUser(userData.data)
    } catch (error) {
      showPopUp(RESULT_REQUEST.ERROR)
    } finally {
      setLoading(false)
    }
  }

  return loading ? (
    <Loader
      type="avia"
      marginTop={150}
    />
  ) : (
    <div className={s.wrapper}>
      <HeaderGoBack
        title={t(ProfileTypes[type as IProfileTypes].name)}
        buttonWhite
      />
      <form
        className={s.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <EditDetailForm
          setValue={setValue}
          register={register}
          control={control}
          errors={errors}
          options={{
            showLabel: true,
            citizenships: citizenshipsData,
          }}
        />

        <ButtonContinue>
          <span className={clsx(s.save, 'semi-bold-17')}>{t('save')}</span>
        </ButtonContinue>
      </form>
    </div>
  )
})

export default EditDetailProfile
